/**
 * Wording of the parts rendered by the browser.
 *
 * A page carries the texts its scripts need inside a JSON island written by
 * `base.html`, so a toast, an empty state or a confirmation speaks the
 * language picked in the header instead of always speaking Slovak.
 *
 * Every lookup passes the Slovak wording as the fallback: without the island
 * (a partial render, a unit test) the interface still says something sensible
 * instead of showing a bare key.
 */

const ISLAND = "[data-ui-texts]";

const PLACEHOLDER = /\{(\w+)\}/g;

let texts: Record<string, string> | null = null;

/** Read the island of the current document, once per page. */
function catalogue(): Record<string, string> {
    if (texts !== null) {
        return texts;
    }

    const island = document.querySelector<HTMLElement>(ISLAND);

    try {
        texts = island?.textContent ? (JSON.parse(island.textContent) as Record<string, string>) : {};
    } catch {
        texts = {};
    }

    return texts;
}

/** Forget the island, used by the tests and after a language switch. */
export function resetTexts(): void {
    texts = null;
}

/**
 * Return the text of `key`, or `fallback` when the page does not carry it.
 *
 * `params` fills the `{name}` placeholders of the text, so a sentence with a
 * counter stays one translatable string instead of being glued together.
 */
export function text(key: string, fallback: string, params: Record<string, string | number> = {}): string {
    const value = catalogue()[key] ?? fallback;

    return value.replace(PLACEHOLDER, (match, name: string) => {
        const replacement = params[name];

        return replacement === undefined ? match : String(replacement);
    });
}
